/**
 * Raise a draft invoice by hand — the other origin beside the case engine's
 * `raise_invoice` action.
 *
 * **A plan prices the first line and then lets go.** Choosing a plan reads its
 * `prodesk_pricing` row once, on the server, and what comes back is an ordinary
 * line the firm may edit. Nothing re-reads the plan afterwards, which is the
 * property that makes the invoice's PDF still match its snapshot a year later.
 *
 * **The billable expenses are offered here rather than on the invoice screen**,
 * because pulling a case's out-of-pocket costs onto the fee note is one act to
 * the person doing it (FR-G5), not a second visit.
 *
 * **w5a added the case and its logged time.** Two things followed from one
 * field. An invoice with no case never reaches a case's timeline — which is
 * half of why the Wave 4 close found the `invoice` and `receipt` timeline
 * sources answering with nothing to answer with — and logged time belongs to a
 * case, so FR-E11's hours could not reach an invoice without one. The Time
 * section appears only when the firm's `hours_price_invoices` switch is on and
 * the chosen case has unbilled time: a section that offered rows the server
 * would refuse is the shape the Wave 4 close found five of.
 *
 * **Wave 15D** (C14 §10.6 item 7) laid it out as the Branches dialog is: the
 * kit's fields in a two-column grid that is one column on a phone, the failure
 * pinned under the title, and Cancel beside the one action at the foot.
 *
 * **C27 (ADR-0056 §1–§4; UAT reports #5 and #12):** the place of supply is
 * **chosen from the state list**, not typed — `27`, `MH` or a typo never
 * equalled *Maharashtra*, and the tax split silently stayed CGST + SGST. It is
 * prefilled from the chosen client's state when the record has one (IGST Act
 * s.12(2): for a service, the recipient's location), else the firm's default.
 * When the firm's own state is still unknown the server refuses rather than
 * guessing, and the refusal is drawn under the field with the way to fix it.
 * Beside it, *Tax payable on reverse charge*: shown as the chosen plan's
 * service default, and sent only once the person flips it — left alone, the
 * server takes that same default, so the screen and the invoice cannot
 * disagree about what "default" meant.
 */

import { Button, Checkbox, Dialog, Input, Select, Switch } from '@fiveox/ui'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type BillableTime,
  type BillingVocabulary,
  type Expense,
  FIRM_STATE_HREF,
  type LineDraft,
  billing,
  isFirmStateUnknown,
  rupees,
} from '../../lib/api/billing'
import { type Case, cases } from '../../lib/api/cases'
import { type ClientRecord, clients } from '../../lib/api/clients'
import { firm } from '../../lib/api/firm'
import { type Service, services } from '../../lib/api/services'
import { OWN_STATE, isState, placeOfSupplyOptions } from '../Firm/settings/rules'
import { CLIENT_PAGE, DialogFooter } from './shared'

/**
 * Where an invoice for this client is supplied, before anybody has chosen:
 * the client's own state when the record has one from the list, else the
 * firm's default, else *the firm's own state*. A default that is not on the
 * list — free text saved before C27 — is not offered as if it were.
 */
export function placeForClient(
  client: Pick<ClientRecord, 'state'> | undefined,
  firmDefault: string | null | undefined,
): string {
  if (client && isState(client.state)) return client.state as string
  return isState(firmDefault) ? (firmDefault as string) : OWN_STATE
}

/** Whether the chosen plan's service starts an invoice on reverse charge
 *  (C27, ADR-0056 §4). `false` with no plan — a hand-typed line has no
 *  service to take a default from. */
export function planReverseCharge(catalog: Service[], planId: string): boolean {
  if (!planId) return false
  const service = catalog.find((s) => (s.plans ?? []).some((p) => p.id === planId))
  return Boolean(service?.reverse_charge)
}

const EMPTY_LINE: LineDraft = {
  description: '',
  unit_amount: '',
  quantity: '1',
  gst_treatment: 'taxable',
  gst_rate: '18',
}

export function NewInvoiceDialog({
  onClose,
  onCreated,
  clientId,
}: {
  onClose: () => void
  onCreated: (invoiceId: string) => void
  clientId?: string
}) {
  const navigate = useNavigate()
  const [book, setBook] = useState<ClientRecord[]>([])
  const [catalog, setCatalog] = useState<Service[]>([])
  const [vocab, setVocab] = useState<BillingVocabulary | null>(null)
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [chosen, setChosen] = useState<string[]>([])
  const [caseList, setCaseList] = useState<Case[]>([])
  const [caseId, setCaseId] = useState('')
  const [hours, setHours] = useState<BillableTime[]>([])
  const [chosenHours, setChosenHours] = useState<string[]>([])
  const [client, setClient] = useState(clientId ?? '')
  const [plan, setPlan] = useState('')
  const [place, setPlace] = useState(OWN_STATE)
  const [placeError, setPlaceError] = useState<{ text: string; settings: boolean } | null>(null)
  const [firmState, setFirmState] = useState<string | null>(null)
  /** `null` until the person flips it: the default is then the server's. */
  const [reverseCharge, setReverseCharge] = useState<boolean | null>(null)
  const [due, setDue] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void Promise.all([
      clients.list({ status: 'active', limit: CLIENT_PAGE }),
      services.list(),
      billing.vocabulary(),
    ])
      .then(([c, s, v]) => {
        setBook(c.data)
        setCatalog(s.data.services)
        setVocab(v.data)
        if (v.data.firm_supply_state !== undefined) {
          setFirmState(v.data.firm_supply_state)
        } else {
          // A vocabulary from before C27 carries no `firm_supply_state`: read the
          // profile, the way Settings → Billing does. Only the option's label
          // depends on it, so a refusal (no `settings:can_view`) costs nothing.
          void firm
            .read()
            .then((body) => setFirmState(body.data.profile.state ?? null))
            .catch(() => undefined)
        }
      })
      .catch(() => setError('The clients and services could not be loaded.'))
  }, [])

  // The place of supply follows the client (C27, ADR-0056 §1). Re-prefilled
  // when the client changes, because the last client's state is the one value
  // that is certainly wrong for this one.
  useEffect(() => {
    if (!vocab) return
    setPlace(placeForClient(book.find((c) => c.id === client), vocab.default_place_of_supply))
    setPlaceError(null)
  }, [client, book, vocab])

  // The client's unbilled, billable expenses (FR-G5). Re-read when the client
  // changes, because an expense belongs to a client and offering another
  // client's would be the tenancy mistake the server refuses anyway.
  useEffect(() => {
    if (!client) {
      setExpenses([])
      setChosen([])
      return
    }
    void billing
      .expenses({ client_id: client, billable_unbilled: true, limit: 100 })
      .then((r) => setExpenses(r.data))
      .catch(() => setExpenses([]))
    void cases
      .queue('all', { client_id: client, limit: 100 })
      .then((r) => setCaseList(r.data))
      .catch(() => setCaseList([]))
    setChosen([])
    setCaseId('')
  }, [client])

  // The chosen case's unbilled time (FR-E11, T-3.6). Read per case rather than
  // per client: hours are logged against a case, the server refuses an entry
  // from another one, and a picker that offered them would be offering a
  // refusal.
  useEffect(() => {
    setChosenHours([])
    if (!client || !caseId || !vocab?.hours_price_invoices) {
      setHours([])
      return
    }
    void billing
      .unbilledTime(caseId, client)
      .then((r) => setHours(r.data))
      .catch(() => setHours([]))
  }, [client, caseId, vocab?.hours_price_invoices])

  const plans = catalog.flatMap((service) =>
    (service.plans ?? []).map((p) => ({
      id: p.id,
      label: `${service.name} — ${p.name}`,
    })),
  )

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)))

  const reverseChargeShown = reverseCharge ?? planReverseCharge(catalog, plan)

  const submit = async () => {
    setSaving(true)
    setError(null)
    setPlaceError(null)
    try {
      const typed = lines.filter((line) => line.description.trim() && line.unit_amount.trim())
      const resp = await billing.createInvoice({
        client_id: client,
        case_id: caseId || null,
        plan_id: plan || null,
        lines: typed.map((line) => ({
          ...line,
          // An exempt line carries no rate, and the server refuses one that
          // does. Cleared here so a firm that switches a line to exempt does
          // not have to also clear a field it can no longer see.
          gst_rate: line.gst_treatment === 'exempt' ? null : line.gst_rate,
        })),
        expense_ids: chosen,
        // Sent only when something is ticked: an empty `hours` block would make
        // the server check the firm's switch and refuse a firm that has it off
        // and billed no time at all.
        hours: chosenHours.length ? { time_entry_ids: chosenHours } : undefined,
        // *The firm's own state* is sent as that state when it is known, so
        // a firm whose default place of supply is elsewhere is not billed
        // there by a choice that said otherwise.
        place_of_supply: place === OWN_STATE ? firmState : place,
        // Sent only once flipped — see the module docstring.
        reverse_charge: reverseCharge ?? undefined,
        due_date: due || null,
      })
      onCreated(resp.data.id)
      navigate(`/billing/invoices/${resp.data.id}`)
    } catch (err) {
      if (isFirmStateUnknown(err)) {
        setPlaceError({ text: (err as ApiError).message, settings: true })
      } else if (err instanceof ApiError && err.details?.field === 'place_of_supply') {
        setPlaceError({ text: err.message, settings: false })
      } else {
        setError(err instanceof ApiError ? err.message : 'The invoice could not be created.')
      }
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title="New invoice" error={error} width="xl">
      <div className={FORM_GRID}>
        <FormRow label="Client" htmlFor="invoice-client" required>
          <Select
            id="invoice-client"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            disabled={Boolean(clientId)}
            required
            aria-required="true"
          >
            {/* A placeholder for a required value, not a choice (UAT #13). */}
            <option value="" disabled hidden>
              Choose a client…
            </option>
            {book.map((c) => (
              <option key={c.id} value={c.id}>
                {c.legal_name}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Price from a plan" htmlFor="invoice-plan" optional>
          <Select id="invoice-plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="">Type the lines myself</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Case" htmlFor="invoice-case" optional>
          <Select
            id="invoice-case"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            disabled={!client}
            hint={
              client
                ? "Puts the invoice on the case's timeline — and is what lets its logged time be billed."
                : 'Choose a client first — a case belongs to one.'
            }
          >
            <option value="">Not on a case</option>
            {caseList.map((row) => (
              <option key={row.id} value={row.id}>
                {row.number} — {row.title}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Place of supply" htmlFor="invoice-place">
          <Select
            id="invoice-place"
            value={place}
            onChange={(e) => {
              setPlace(e.target.value)
              setPlaceError(null)
            }}
            error={placeError?.text}
            hint="Decides whether the tax splits as CGST and SGST, or as IGST. Filled in from the client’s state."
          >
            {placeOfSupplyOptions(firmState).map((option) => (
              <option key={option.value || 'own'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {/* The kit draws the refusal under the field; the way to fix it
              goes right beneath, because the fix is on another screen. */}
          {placeError?.settings && (
            <p className="mt-1 text-xs" data-place-error>
              <Link to={FIRM_STATE_HREF} className="text-destructive font-medium underline">
                Open Settings → Your firm
              </Link>
            </p>
          )}
        </FormRow>
        <FormRow label="Due date" htmlFor="invoice-due">
          <Input id="invoice-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </FormRow>
        <FormRow label="Reverse charge" htmlFor="invoice-reverse-charge">
          <div>
            <Switch
              id="invoice-reverse-charge"
              checked={reverseChargeShown}
              onChange={(e) => setReverseCharge(e.target.checked)}
              label="Tax payable on reverse charge"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              {reverseChargeShown
                ? 'The GST is worked out and shown on the invoice as payable by the client. It is not added to what they owe you.'
                : 'Off for most invoices. A service can turn it on by default.'}
            </p>
          </div>
        </FormRow>
      </div>

      <fieldset className="border-border mt-6 space-y-3 border-t pt-5">
        <legend className="text-foreground mb-1 text-sm font-semibold">Lines</legend>
        {lines.map((line, index) => (
          <div
            key={index}
            data-line={index}
            className="border-border grid grid-cols-2 gap-2 rounded-xl border p-3 sm:grid-cols-12 sm:rounded-none sm:border-0 sm:p-0"
          >
            <div className="col-span-2 sm:col-span-5">
              <Input
                aria-label={`Line ${index + 1}: what it is for`}
                value={line.description}
                onChange={(e) => setLine(index, { description: e.target.value })}
                placeholder="What this line is for"
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                aria-label={`Line ${index + 1}: SAC`}
                value={line.sac_code ?? ''}
                onChange={(e) => setLine(index, { sac_code: e.target.value })}
                placeholder="SAC"
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                aria-label={`Line ${index + 1}: amount`}
                value={line.unit_amount}
                onChange={(e) => setLine(index, { unit_amount: e.target.value })}
                placeholder="Amount"
                inputMode="decimal"
              />
            </div>
            <div className="sm:col-span-2">
              <Select
                aria-label={`Line ${index + 1}: GST`}
                value={line.gst_treatment ?? 'taxable'}
                onChange={(e) =>
                  setLine(index, {
                    gst_treatment: e.target.value as 'taxable' | 'exempt',
                    gst_rate: e.target.value === 'exempt' ? null : (line.gst_rate ?? '18'),
                  })
                }
              >
                <option value="taxable">Taxable</option>
                <option value="exempt">Exempt</option>
              </Select>
            </div>
            <div className="sm:col-span-1">
              <Input
                aria-label={`Line ${index + 1}: GST rate %`}
                value={line.gst_rate ?? ''}
                onChange={(e) => setLine(index, { gst_rate: e.target.value })}
                placeholder="%"
                inputMode="decimal"
                disabled={line.gst_treatment === 'exempt'}
                title={line.gst_treatment === 'exempt' ? 'An exempt line carries no rate.' : undefined}
              />
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setLines((c) => [...c, { ...EMPTY_LINE }])}
        >
          <Plus size={14} aria-hidden /> Add a line
        </Button>
      </fieldset>

      {expenses.length > 0 && (
        <fieldset className="border-border mt-6 space-y-2 border-t pt-5">
          <legend className="text-foreground text-sm font-semibold">Pass-through expenses</legend>
          <p className="text-muted-foreground text-xs">
            Costs recorded against this client’s cases and not yet billed. Each becomes a line,
            taxed as it was marked.
          </p>
          {expenses.map((expense) => (
            <Checkbox
              key={expense.id}
              checked={chosen.includes(expense.id)}
              onChange={(e) =>
                setChosen((current) =>
                  e.target.checked
                    ? [...current, expense.id]
                    : current.filter((id) => id !== expense.id),
                )
              }
              label={`${expense.description} — ${rupees(expense.amount)} (${expense.incurred_on})`}
            />
          ))}
        </fieldset>
      )}

      {hours.length > 0 && (
        <fieldset className="border-border mt-6 space-y-2 border-t pt-5">
          <legend className="text-foreground text-sm font-semibold">Time logged on this case</legend>
          <p className="text-muted-foreground text-xs">
            {/* The amount beside each row is the server's, worked out from the
                rate in paise and the minutes — not from decimal hours, which
                would be seven rupees out on a twenty-five minute entry. */}
            Each becomes its own line at the rate it was logged at. Time logged before your firm
            switched hours pricing on carries no rate and is not offered.
          </p>
          {hours.map((entry) => (
            <Checkbox
              key={entry.id}
              checked={chosenHours.includes(entry.id)}
              onChange={(e) =>
                setChosenHours((current) =>
                  e.target.checked
                    ? [...current, entry.id]
                    : current.filter((id) => id !== entry.id),
                )
              }
              label={`${entry.spent_on} — ${entry.duration} at ${rupees(entry.rate)}/hour = ${rupees(entry.amount)}`}
            />
          ))}
        </fieldset>
      )}

      {vocab?.hours_price_invoices && caseId && hours.length === 0 && (
        <p className="text-muted-foreground mt-4 text-xs">No unbilled time on this case.</p>
      )}

      {vocab && (
        <p className="text-muted-foreground mt-6 text-xs">
          The number is drawn when the invoice is issued, from the {vocab.financial_year} series —
          a draft takes none, so your numbering has no gaps for invoices that never went out.
        </p>
      )}

      <DialogFooter onCancel={onClose} busy={saving}>
        <span title={client ? undefined : 'Choose a client first.'}>
          <Button onClick={() => void submit()} disabled={!client} loading={saving}>
            Create draft
          </Button>
        </span>
      </DialogFooter>
    </Dialog>
  )
}
